/**
 * Smart Shikka AI Core — PR95
 * Conversation Persistence Schema + Store — Approval Required / No Provider Call
 * Registry — Deep Frozen — No Provider Call — Gated Persistence
 */
function deepFreeze(obj) {
    if (obj && typeof obj === "object") {
        Object.freeze(obj);
        for (const key of Object.keys(obj)) {
            const val = obj[key];
            if (val && typeof val === "object" && !Object.isFrozen(val)) {
                deepFreeze(val);
            }
        }
    }
    return obj;
}
const BLOCKED_ACTIONS = [
    "agent_execution", "child_process", "model_inference",
    "old_assistant_alias", "provider_call", "secret_literal",
    "shell_command", "tool_execution", "unapproved_db_write",
    "unapproved_file_write", "unapproved_network_call",
    "unknown_provider_call", "unknown_tool_execution",
    "unredacted_secret", "unsafe_env_dump",
    "runtime_unlock", "gate_unlock", "browser_action",
    "desktop_action", "render_document", "inject_icon_runtime",
    "workflow_execution", "message_send", "chat_runtime_start",
    "user_prompt_send", "secret_read", "file_read", "file_write",
];
const COMMON_SAFETY = {
    previewOnly: true,
    readonlyOnly: true,
    providerCallAllowedInPr95: false,
    secretLiteralAllowedInPr95: false,
    secretReadAllowedInPr95: false,
    modelInferenceAllowedInPr95: false,
    runtimeUnlockAllowedInPr95: false,
    gateUnlockAllowedInPr95: false,
    agentExecutionAllowedInPr95: false,
    toolExecutionAllowedInPr95: false,
    workflowExecutionAllowedInPr95: false,
    messageSendAllowedInPr95: false,
    chatRuntimeAllowedInPr95: false,
    documentRenderAllowedInPr95: false,
    iconInjectionAllowedInPr95: false,
    browserAutomationAllowedInPr95: false,
    desktopAutomationAllowedInPr95: false,
    fileMutationAllowedInPr95: false,
    fileReadAllowedInPr95: false,
    networkAllowedInPr95: false,
    dbWriteAllowedInPr95: false,
    supabaseAllowedInPr95: false,
    shellCommandAllowedInPr95: false,
    childProcessAllowedInPr95: false,
    userPromptAllowedInPr95: false,
    requiresOwnerApproval: true,
    requiresUserApproval: true,
};
const registry = [
    {
        id: "conversation_persistence_summary",
        displayName: "Conversation Persistence Summary",
        description: "Summary of conversation persistence schema and store contracts. Gated writes, sanitized inputs, no provider call.",
        sourceModules: ["smart-shikka-assistant-conversation-persistence", "smart-shikka-assistant-readonly-conversation-state-preview"],
        targetPreviewModules: ["smart-shikka-assistant-conversation-persistence"],
        relatedRouteIds: ["capability_discovery_request"],
        relatedUiPreviewIds: ["capability_discovery_route_card"],
        relatedChatPreviewIds: ["readonly_message_thread_preview"],
        relatedConversationStatePreviewIds: ["readonly_conversation_state_snapshot"],
        relatedApprovalStatePreviewIds: ["readonly_approval_summary_state"],
        relatedActionRequestPreviewIds: ["readonly_action_request_summary"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Persistence is gated and approval-required", "No provider call during persist", "Schema-only in PR95"],
        futurePrScope: ["Live persistence with dual approval", "Migration execution"],
        notes: ["DB schema contracts defined", "No runtime writes in PR95"],
    },
    {
        id: "conversation_schema_contract",
        displayName: "Conversation Schema Contract",
        description: "Defines the conversation table schema: id, title, created_at, updated_at, owner_id, status. No migration execution in PR95.",
        sourceModules: ["smart-shikka-assistant-conversation-persistence"],
        targetPreviewModules: ["smart-shikka-assistant-conversation-persistence"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: ["readonly_conversation_state_snapshot"],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Schema contract only", "No DDL execution", "Migration is future scope"],
        futurePrScope: ["Schema migration execution with approval"],
        notes: ["Fields: id, title, created_at, updated_at, owner_id, status"],
    },
    {
        id: "message_schema_contract",
        displayName: "Message Schema Contract",
        description: "Defines the message table schema: id, conversation_id, role, content_redacted, timestamp, sequence. No write execution in PR95.",
        sourceModules: ["smart-shikka-assistant-conversation-persistence"],
        targetPreviewModules: ["smart-shikka-assistant-conversation-persistence"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: ["readonly_message_thread_preview"],
        relatedConversationStatePreviewIds: ["readonly_message_thread_state"],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Message schema contract only", "No write execution", "Content must be redacted before persist"],
        futurePrScope: ["Message write with redaction enforcement"],
        notes: ["Fields: id, conversation_id, role, content_redacted, timestamp, sequence"],
    },
    {
        id: "redacted_transcript_schema_contract",
        displayName: "Redacted Transcript Schema Contract",
        description: "Defines audit transcript schema with all PII/secrets redacted before storage. No raw content persisted.",
        sourceModules: ["smart-shikka-assistant-conversation-persistence"],
        targetPreviewModules: ["smart-shikka-assistant-conversation-persistence"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: ["readonly_audit_transcript_state"],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: ["readonly_audit_action_request_placeholder"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["All content redacted before persist", "No PII storage", "No raw provider response stored"],
        futurePrScope: ["Redaction enforcement at persist boundary"],
        notes: ["Content stored as [REDACTED_CONTENT]", "Secrets stored as [REDACTED_SECRET_REF]"],
    },
    {
        id: "approved_conversation_write_gate",
        displayName: "Approved Conversation Write Gate",
        description: "Gate requiring dual approval before any conversation write. Owner + user approval required. No write without gate open.",
        sourceModules: ["smart-shikka-assistant-conversation-persistence", "smart-shikka-assistant-readonly-approval-state-preview"],
        targetPreviewModules: ["smart-shikka-assistant-conversation-persistence"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: ["readonly_dual_approval_required_state"],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Dual approval required for write", "Owner must approve", "User must approve", "Gate locked in PR95"],
        futurePrScope: ["Approved write execution"],
        notes: ["Write gate locked in PR95", "No DB writes without explicit approval"],
    },
    {
        id: "conversation_read_model_contract",
        displayName: "Conversation Read Model Contract",
        description: "Defines the read-only query contract for conversations. Read returns redacted content only. No raw data exposed.",
        sourceModules: ["smart-shikka-assistant-conversation-persistence"],
        targetPreviewModules: ["smart-shikka-assistant-conversation-persistence"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: ["readonly_conversation_state_snapshot"],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Read returns redacted content only", "No raw data in read model"],
        futurePrScope: ["Read query execution with redaction"],
        notes: ["Read model always returns redacted content", "No PII in query results"],
    },
    {
        id: "conversation_delete_disabled_gate",
        displayName: "Conversation Delete Disabled Gate",
        description: "Delete operations permanently disabled in PR95. Conversations are append-only with soft-delete metadata.",
        sourceModules: ["smart-shikka-assistant-conversation-persistence"],
        targetPreviewModules: ["smart-shikka-assistant-conversation-persistence"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Delete operations disabled", "Append-only design", "Soft-delete via status field only"],
        futurePrScope: ["Soft-delete status update with approval"],
        notes: ["No hard delete ever", "status field used for soft-delete"],
    },
    {
        id: "secret_redaction_before_persist",
        displayName: "Secret Redaction Before Persist",
        description: "All secrets, API keys, and PII must be redacted before any persistence operation. No unredacted data stored.",
        sourceModules: ["smart-shikka-assistant-conversation-persistence", "smart-shikka-assistant-provider-settings-secret-reference"],
        targetPreviewModules: ["smart-shikka-assistant-conversation-persistence"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: [],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["No unredacted secrets stored", "Redaction before persist is mandatory", "No PII in DB"],
        futurePrScope: ["Live redaction enforcement at persist boundary"],
        notes: ["Secrets → [REDACTED_SECRET_REF]", "PII → [REDACTED_PII]", "Provider responses → [REDACTED_RESPONSE]"],
    },
    {
        id: "provider_call_disabled_for_persistence",
        displayName: "Provider Call Disabled for Persistence",
        description: "No provider calls triggered by persistence operations. Persistence is purely data storage, no AI inference.",
        sourceModules: ["smart-shikka-assistant-conversation-persistence", "smart-shikka-assistant-provider-healthcheck"],
        targetPreviewModules: ["smart-shikka-assistant-conversation-persistence"],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: ["provider_call_blocked_card"],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: ["readonly_provider_call_approval_blocked_state"],
        relatedActionRequestPreviewIds: ["readonly_provider_action_request_blocked"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["No provider call during persistence", "Persistence is data storage only", "No AI inference triggered by write"],
        futurePrScope: ["Provider-assisted summarization with separate approval"],
        notes: ["provider_call blocked", "model_inference blocked", "Purely data operation"],
    },
    {
        id: "future_chat_send_persistence_ready",
        displayName: "Future Chat Send Persistence Ready",
        description: "Placeholder for future chat-send-triggered persistence. Locked in PR95. Requires chat runtime approval gate.",
        sourceModules: ["smart-shikka-assistant-conversation-persistence"],
        targetPreviewModules: [],
        relatedRouteIds: [],
        relatedUiPreviewIds: [],
        relatedChatPreviewIds: ["blocked_message_send_card"],
        relatedConversationStatePreviewIds: [],
        relatedApprovalStatePreviewIds: [],
        relatedActionRequestPreviewIds: [],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Chat send persistence locked", "Future scope", "Requires chat runtime approval"],
        futurePrScope: ["Chat-send-triggered persistence with dual approval", "Message persistence after chat runtime unlock"],
        notes: ["Gate locked in PR95", "No chat send triggers persistence"],
    },
];
deepFreeze(registry);
export function getSmartShikkaAssistantConversationPersistenceEntries() {
    return registry;
}
export function getSmartShikkaAssistantConversationPersistenceEntryById(id) {
    return registry.find((e) => e.id === id);
}
//# sourceMappingURL=smartShikkaAssistantConversationPersistenceRegistry.js.map